import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { artistInfo } from '../mock';

const Navigation = ({ isScrolled }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [visibleLetters, setVisibleLetters] = useState([false, false, false, false]);
  
  useEffect(() => {
    // Reset and animate letters appearing one at a time with cursive drawing effect
    setVisibleLetters([false, false, false, false]);
    
    const timers = ['J', 'L', 'E', 'E'].map((_, index) => 
      setTimeout(() => {
        setVisibleLetters(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * 500 + 400) // 400ms initial delay, then 500ms between each letter for cursive effect
    );
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, []);

  const navLinks = [
    { name: 'Events', href: '#events' },
    { name: 'About', href: '#about' },
    { name: 'Music', href: '#music' },
    // { name: 'Videos', href: '#videos' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Booking', href: '#booking' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    try {
      const element = document.querySelector(href);
      if (element) {
        // Account for fixed header height - use dynamic header height or fallback to 100px
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 100;
        const offset = headerHeight + 20; // Header height + extra padding
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: Math.max(0, offsetPosition), // Ensure we don't scroll to negative position
          behavior: 'smooth'
        });
        setIsMobileMenuOpen(false);
      } else {
        // Element not found - log warning but don't throw error
        if (process.env.NODE_ENV === 'development') {
        }
      }
    } catch (error) {
      // Catch any errors and prevent them from breaking the page
      if (process.env.NODE_ENV === 'development') {
      }
    }
  };

  return (
    <>
      <header
        id="main-header"
        className={`fixed top-0 w-full z-50 transition-all duration-400 nav-header-base ${
          isScrolled
            ? 'bg-black border-b border-white/30 backdrop-blur-md shadow-lg'
            : 'bg-black/95 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-6 lg:px-[7.6923%] h-full flex items-center justify-between gap-3 md:gap-4 lg:gap-6">
          {/* Logo */}
          <a 
            href="#" 
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center dark-transition hover:opacity-80 flex-shrink-0 z-10 logo-container-base"
            aria-label="JLEE - Home"
          >
            <span className="relative inline-flex items-center gap-0 md:gap-0.5 logo-letter-container" aria-label="JLEE">
              {['J', 'L', 'E', 'E'].map((letter, index) => (
                <span
                  key={`${letter}-${index}`}
                  className={`logo-letter-base ${visibleLetters[index] ? 'logo-letter-animated' : 'logo-letter-hidden'}`}
                  aria-hidden="true"
                >
                  {/* White outer outline layer */}
                  <span 
                    className="logo-letter-outline-white jlee-text-responsive"
                    aria-hidden="true"
                  >
                    {letter}
                  </span>
                  {/* Black middle outline layer */}
                  <span 
                    className="logo-letter-outline-black jlee-text-responsive"
                    aria-hidden="true"
                  >
                    {letter}
                  </span>
                  {/* Gray fill layer */}
                  <span 
                    className="logo-letter-fill jlee-text-responsive"
                    aria-hidden="true"
                  >
                    {letter}
                  </span>
                </span>
              ))}
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="header-nav-desktop hidden md:flex items-center gap-3 md:gap-4 lg:gap-5 xl:gap-6 flex-shrink-0 ml-auto nav-fit-content">
            {navLinks.map((link) => (
              <a
                key={link.name}
                id={`header-nav-link-${link.name.toLowerCase()}`}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="header-nav-link-desktop nav-fit-content"
              >
                <span className="header-nav-link-text">{link.name}</span>
              </a>
            ))}
            <a
              href="#booking"
              onClick={(e) => scrollToSection(e, '#booking')}
              className="btn-primary"
            >
              <span className="text-black">Book</span>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="header-mobile-menu-button md:hidden p-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

          {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="header-mobile-menu-backdrop fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Dropdown Menu */}
          <div className="header-mobile-menu-dropdown fixed top-[70px] right-2 bg-black border border-white/30 rounded-lg shadow-2xl z-50 md:hidden w-auto min-w-[140px] max-w-[75vw]">
            <nav className="header-nav-mobile flex flex-col gap-0.5 p-1.5">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  id={`header-nav-link-mobile-${link.name.toLowerCase()}`}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="header-nav-link-mobile px-2.5 py-1.5 rounded hover:bg-white/10 transition-colors"
                >
                  <span className="header-nav-link-mobile-text">{link.name}</span>
                </a>
              ))}
              <a
                href="#booking"
                onClick={(e) => scrollToSection(e, '#booking')}
                className="btn-primary mt-1.5 mx-1.5 text-sm py-2"
              >
                <span className="text-white">Book</span>
              </a>
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Navigation;